import { Card } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { STAGES, type StageKey } from "./agentResearchCopy";

/**
 * ① Start ② AI research ③ Findings ④ Your turn ⑤ Review & save (D8). Every
 * stage is clickable at any time: the rail says where the reader is, never
 * where they are allowed to go. Wraps with its container rather than at a
 * viewport breakpoint (Table & Tile standard).
 */
export function StageRail({
  current,
  onSelect,
  badges,
}: {
  current: StageKey;
  onSelect: (stage: StageKey) => void;
  /** A short count beside a stage, e.g. findings waiting for review. */
  badges?: Partial<Record<StageKey, string>>;
}) {
  return (
    <Card className="p-space-2">
      <nav aria-label="Research stages">
        <ol className="m-0 flex flex-wrap list-none gap-space-1 p-0">
          {STAGES.map((s, i) => {
            const selected = s.key === current;
            return (
              <li key={s.key} className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => onSelect(s.key)}
                  aria-current={selected ? "step" : undefined}
                  data-testid={`agent-research-stage-${s.key}`}
                  className={cn(
                    "flex w-full items-center gap-space-2 rounded-md px-space-3 py-space-2 text-left",
                    "transition-colors duration-fast ease-standard",
                    "focus-visible:outline-none focus-visible:shadow-goldRing",
                    selected ? "bg-gold-tint" : "hover:bg-white-warm",
                  )}
                >
                  <span
                    className={cn(
                      "inline-flex h-space-4 w-space-4 shrink-0 items-center justify-center rounded-pill border text-caption",
                      selected ? "border-gold bg-gold text-onyx" : "border-border-light text-gray-mid",
                    )}
                    aria-hidden
                  >
                    {i + 1}
                  </span>
                  <span className={cn("text-small", selected ? "font-semibold text-onyx" : "text-onyx")}>
                    {s.label}
                  </span>
                  {badges?.[s.key] && (
                    <span className="ml-auto text-caption text-gray-mid">{badges[s.key]}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
      </nav>
    </Card>
  );
}
